import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  authClient,
  isMfaEnrollmentChallenge,
  isMfaVerifyChallenge,
  isPasswordChangeChallenge,
  useSwitchAccountMutation,
} from '@/shared/api/auth';
import { redirectToPostAuthDestination } from '@/shared/auth/postAuthRedirect';
import { getLoginFlowError, isSafeReturnUrl, resolveSafeReturnUrl } from '@/shared/utils';

export type SwitchStatus = 'loading' | 'error';

export interface AccountSwitchParams {
  /** Target account. `null` when the hand-off was malformed (missing/invalid `userId`). */
  userId: number | null;
  /** Optional location to activate in the target account. */
  locationId?: number;
  /** Same-origin path to return to after the switch; validated only at the redirect. */
  returnUrl: string | null;
}

const INVALID_TARGET_MESSAGE = 'This account switch link is invalid or has expired. Please try again.';

/**
 * Drives the `/switch` hand-off. Panels redirect here for every account switch (never calling
 * `switch-account` themselves). On mount it bootstraps the current session, issues `switch-account`
 * inside `auth-ui` (without opting into challenges — gates are login-time only), adopts the switched
 * session, and redirects to `returnUrl`. No session at all routes to login. Errors surface without
 * adopting, so the pre-switch session stays intact. Should the backend nevertheless answer with a
 * challenge body (deploy skew, unforeseen case), nothing is adopted and the user exits to `/login`,
 * where the login wizard resolves every gate after a fresh sign-in.
 */
export const useAccountSwitch = ({ userId, locationId, returnUrl }: AccountSwitchParams) => {
  const navigate = useNavigate();
  const { switchAccount } = useSwitchAccountMutation();

  const [status, setStatus] = useState<SwitchStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    // Run exactly once: a switch issues a session, and React StrictMode double-invokes effects in
    // development — a genuine second run would spend a second switch for nothing.
    if (hasRun.current) {
      return;
    }
    hasRun.current = true;

    const run = async () => {
      // Bootstrap the current session (refresh cookie → access token). A transient failure (network
      // or 5xx) must NOT bounce the user to login — their pre-switch session is still valid — so a
      // thrown error surfaces as a retryable error state with the session intact. Only a clean `null`
      // (guest, no session) routes to login, preserving `returnUrl` so login can send them back.
      let accessToken: string | null;
      try {
        accessToken = await authClient.ensureAccessToken();
      } catch (bootstrapError) {
        setError(getLoginFlowError(bootstrapError));
        setStatus('error');

        return;
      }

      if (!accessToken) {
        navigate({
          to: '/login',
          search: isSafeReturnUrl(returnUrl) ? { returnUrl } : {},
          replace: true,
        });

        return;
      }

      // A malformed hand-off never reaches the backend: surface it with the pre-switch session intact.
      if (userId === null) {
        setError(INVALID_TARGET_MESSAGE);
        setStatus('error');

        return;
      }

      try {
        const response = await switchAccount({
          userId,
          ...(locationId !== undefined ? { locationId } : {}),
        });

        // Defensive fallback: the request does not opt into challenges, so no challenge should ever
        // arrive — but a legacy backend during a deploy window might still send one. A challenge body
        // carries no session, so adopting it would clear the current session and strand the user on a
        // loader. Never adopt, never start a ceremony here: exit to `/login` (preserving a validated
        // `returnUrl`), where the wizard resolves every gate after a fresh sign-in. The token in the
        // body is dropped without entering a URL, history state, or storage.
        const isChallenge =
          isPasswordChangeChallenge(response) || isMfaVerifyChallenge(response) || isMfaEnrollmentChallenge(response);
        if (isChallenge) {
          navigate({
            to: '/login',
            search: isSafeReturnUrl(returnUrl) ? { returnUrl } : {},
            replace: true,
          });

          return;
        }

        // Adopt the switched session and redirect. We drive the redirect explicitly because the user
        // was already authorized, so there is no guest→authorized transition for `useAuthRedirect` to
        // react to.
        authClient.adoptSession(response);
        redirectToPostAuthDestination();
      } catch (switchError) {
        setError(getLoginFlowError(switchError));
        setStatus('error');
      }
    };

    void run();
  }, [navigate, switchAccount, userId, locationId, returnUrl]);

  // On a surfaced error the user still holds their pre-switch session: send them back where they
  // came from when `returnUrl` is valid, otherwise to login. `replace` so the spent `/auth/switch`
  // entry does not linger in history.
  const dismiss = () => {
    const safeReturnUrl = resolveSafeReturnUrl(returnUrl);
    if (safeReturnUrl) {
      window.location.replace(safeReturnUrl);

      return;
    }

    navigate({ to: '/login', replace: true });
  };

  return { status, error, dismiss };
};
